import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Users, DollarSign, TrendingUp, UserCheck, UserX, Clock, RefreshCw, CloudUpload, Check, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UserStatsTab = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onedrive, setOnedrive] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');

  const fetchStats = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/user-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch user stats:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchOnedriveStatus();
  }, []);

  const fetchOnedriveStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/onedrive/status`, { headers: { Authorization: `Bearer ${token}` } });
      setOnedrive(res.data);
    } catch (err) { console.error('OneDrive status error:', err); }
  };

  const triggerSync = async () => {
    setSyncing(true);
    setSyncMsg('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/admin/onedrive/sync`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSyncMsg('Sync started! Files are backing up in the background.');
      setTimeout(fetchOnedriveStatus, 30000);
    } catch (err) {
      setSyncMsg('Failed to start sync');
    } finally {
      setSyncing(false);
    }
  };

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center py-16">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const statCards = [
    { label: 'Total Users', value: stats.total_users, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Active Subscribers', value: stats.total_active, icon: UserCheck, color: 'text-green-600', bg: 'bg-green-50' },
    { label: 'Monthly ($25/mo)', value: stats.monthly_subscribers, icon: DollarSign, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Annual ($250/yr)', value: stats.annual_subscribers, icon: DollarSign, color: 'text-teal-600', bg: 'bg-teal-50' },
    { label: 'Lifetime', value: stats.lifetime_subscribers, icon: UserCheck, color: 'text-purple-600', bg: 'bg-purple-50' },
    { label: 'Active Trials', value: stats.active_trials, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'Expired Trials', value: stats.expired_trials, icon: UserX, color: 'text-red-600', bg: 'bg-red-50' },
  ];

  return (
    <div className="space-y-6" data-testid="user-stats-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold uppercase tracking-wide">Subscriber & Revenue Stats</h2>
        <Button variant="outline" size="sm" onClick={fetchStats} data-testid="refresh-stats-btn">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Revenue Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="border-2 border-green-200 bg-green-50/50" data-testid="mrr-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-xl">
                <TrendingUp className="w-6 h-6 text-green-700" />
              </div>
              <div>
                <p className="text-sm text-green-700 font-medium">Monthly Recurring Revenue</p>
                <p className="text-3xl font-bold text-green-800">${stats.mrr.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-2 border-blue-200 bg-blue-50/50" data-testid="arr-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-xl">
                <DollarSign className="w-6 h-6 text-blue-700" />
              </div>
              <div>
                <p className="text-sm text-blue-700 font-medium">Annualized Revenue</p>
                <p className="text-3xl font-bold text-blue-800">${stats.arr.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stat Cards Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {statCards.map((s) => (
          <Card key={s.label} className="overflow-hidden">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className={`p-1.5 rounded-lg ${s.bg}`}>
                  <s.icon className={`w-4 h-4 ${s.color}`} />
                </div>
                <span className="text-xs text-muted-foreground font-medium">{s.label}</span>
              </div>
              <p className="text-2xl font-bold">{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Signups Chart */}
      {stats.monthly_signups && stats.monthly_signups.length > 0 && (
        <Card data-testid="signups-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">New Signups (Last 6 Months)</CardTitle>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.monthly_signups} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{ borderRadius: '8px', fontSize: '13px' }}
                    labelStyle={{ fontWeight: 'bold' }}
                  />
                  <Bar dataKey="count" fill="#16a34a" radius={[4, 4, 0, 0]} name="Signups" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Revenue Breakdown */}
      <Card data-testid="revenue-breakdown">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Revenue Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">Monthly</Badge>
                <span className="text-sm">{stats.monthly_subscribers} subscribers x $25/mo</span>
              </div>
              <span className="font-semibold">${(stats.monthly_subscribers * 25).toLocaleString()}/mo</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="bg-teal-100 text-teal-700">Annual</Badge>
                <span className="text-sm">{stats.annual_subscribers} subscribers x $250/yr</span>
              </div>
              <span className="font-semibold">${((stats.annual_subscribers * 250) / 12).toFixed(2)}/mo</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="bg-purple-100 text-purple-700">Lifetime</Badge>
                <span className="text-sm">{stats.lifetime_subscribers} lifetime members</span>
              </div>
              <span className="font-semibold text-muted-foreground">-</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* OneDrive Backup */}
      <Card data-testid="onedrive-backup">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <CloudUpload className="w-5 h-5" /> OneDrive Backup
          </CardTitle>
        </CardHeader>
        <CardContent>
          {onedrive && onedrive.configured ? (
            <div className="space-y-3">
              <div className="flex items-center gap-4 text-sm">
                <span className="text-muted-foreground">Videos backed up: <strong>{onedrive.videos_backed_up}</strong></span>
                <span className="text-muted-foreground">Photos backed up: <strong>{onedrive.photos_backed_up}</strong></span>
              </div>
              <div className="flex items-center gap-3">
                <Button onClick={triggerSync} disabled={syncing} data-testid="onedrive-sync-btn">
                  {syncing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CloudUpload className="w-4 h-4 mr-2" />}
                  {syncing ? 'Starting...' : 'Sync All to OneDrive'}
                </Button>
                {syncMsg && (
                  <span className="text-sm text-green-600 flex items-center gap-1">
                    <Check className="w-4 h-4" /> {syncMsg}
                  </span>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> OneDrive not configured
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UserStatsTab;
